/**
 * Which pandora source(s) are on screen at a given timeline time, for the
 * public viewer's contextual annotation panel. Pure (no DOM / network) so it
 * can be unit-tested and called every frame cheaply.
 */
import { activeClipsAt } from '../format/helpers';
import type { Edit, VideoClip } from '../format/types';

export interface ActivePandoraSource {
  archiveKey: string;
  item: string;
  part: number; // 1-based
  clip: VideoClip;
  /** Source-local media time within this part (before concat offset). */
  mediaTime: number;
}

/**
 * For each video track, the active clips whose source is a pandora video, with
 * the source-local media time under the playhead. Usually 0 or 1 entries (2
 * during a crossfade).
 */
export function activePandoraSourcesAt(edit: Edit, t: number): ActivePandoraSource[] {
  const out: ActivePandoraSource[] = [];
  for (const track of edit.tracks) {
    if (track.kind !== 'video') continue;
    for (const clip of activeClipsAt(track, t)) {
      const src = edit.sources[clip.source];
      if (!src || src.kind !== 'pandora-video') continue;
      const mediaTime = clip.in + (t - clip.start) * (clip.speed ?? 1);
      out.push({
        archiveKey: src.archive,
        item: src.item,
        part: src.part,
        clip,
        mediaTime,
      });
    }
  }
  return out;
}

/**
 * The annotation cue covering concat-time `cT` from a set of subtitle/transcript
 * annotations: `a.in <= cT < a.out`. Returns the first match (pandora layers are
 * time-sorted) or null. Pure — unit-tested.
 */
export function cueAt<A extends { in: number; out: number }>(
  annotations: A[],
  cT: number,
): A | null {
  for (const a of annotations) {
    if (cT >= a.in && cT < a.out) return a;
  }
  return null;
}
